<script lang="ts" setup>
import { InputTypeEnum } from "@/enums/chatEnums";
import { useMate } from "@/stores/mate";
import { ChatSceneCharacter, ChatScenePosition } from "@/types/chat";
import { Mate, MateSession } from "@/types/entity";
import { showConfirmDialog, showFailToast } from "vant";
import { computed, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
const props = defineProps<{
  mateSession: MateSession;
  actionTargets?: string[];
  mate: Mate;
  currentMsgType?: InputTypeEnum;
}>();
const mateStore = useMate();

const { t } = useI18n();
const emit = defineEmits<{
  (e: "update:actionTargets", targets: string[]): void;
  (e: "targetId", id: number): void;
}>();

const characterList = computed(() => {
  const currentPositionCharacterList: ChatSceneCharacter[] = [];
  const otherCharacterList: ChatSceneCharacter[] = [];
  const positionMap = new Map<number, ChatScenePosition>();

  props.mateSession.chatScene?.positionzz?.forEach((v) => {
    positionMap.set(v.id!, v);
  });

  props.mateSession.chatScene?.characterzz?.forEach((v) => {
    if (v.role === 2) return;
    v.currentPosition = positionMap.get(v.currentPositionId!);
    if (v.currentPositionId === playerCurrentPosition.value?.id) {
      currentPositionCharacterList.push(v);
    } else {
      otherCharacterList.push(v);
    }
  });
  return [...currentPositionCharacterList, ...otherCharacterList];
});

const onClickCharacter = (character: ChatSceneCharacter) => {
  if (character.currentPositionId !== playerCurrentPosition.value?.id) {
    return showFailToast(t("Chats.chatScene.edit.notInSamePosition"));
  }
  if (props.currentMsgType === InputTypeEnum.FIGHT) {
    showConfirmDialog({
      message: t("Chats.confirmFight", {
        name: character.mate!.name,
      }),
      confirmButtonText: t("Buttons.confirm"),
      cancelButtonText: t("Buttons.cancel"),
    }).then(() => {
      emit("targetId", character.id!);
    });

    return;
  }
  if (props.actionTargets?.includes(character.mate!.name)) {
    if (props.actionTargets?.length > 1) {
      emit(
        "update:actionTargets",
        props.actionTargets?.filter((target) => target !== character.mate!.name)
      );
      return;
    }
  } else {
    emit("update:actionTargets", [
      ...props.actionTargets!,
      character.mate!.name,
    ]);
  }
};

const playerCurrentPosition = computed(() => {
  if (!props.mateSession.chatScene) return undefined;
  const { characterzz, positionzz } = props.mateSession.chatScene;
  const { currentPositionId } = characterzz?.find((v) => v.role === 2) ?? {};
  return positionzz?.find((v) => v.id === currentPositionId);
});

watch(
  () => playerCurrentPosition.value?.id,
  (val) => {
    const currentPositionCharacterList: ChatSceneCharacter[] = [];
    const positionMap = new Map<number, ChatScenePosition>();
    props.mateSession.chatScene?.characterzz?.forEach((v) => {
      if (v.role === 2) return;
      v.currentPosition = positionMap.get(v.currentPositionId!);
      if (v.currentPositionId === val) currentPositionCharacterList.push(v);
    });

    let targets = props.actionTargets?.slice() || [];

    if (targets.length > 0) {
      targets = targets.filter((v) =>
        currentPositionCharacterList.find((c) => c.mate?.name === v)
      );
    }

    if (targets.length === 0 && currentPositionCharacterList.length > 0) {
      targets = [currentPositionCharacterList[0].mate!.name];
    }
    emit("update:actionTargets", targets);
  },
  {
    immediate: true,
  }
);

const onClickPosition = (position: ChatScenePosition) => {
  showConfirmDialog({
    message: t("Chats.confirmAction", {
      action: t(`Chats.actions.${props.currentMsgType?.toLowerCase()}`),
      name: position.name,
    }),
    confirmButtonText: t("Buttons.confirm"),
    cancelButtonText: t("Buttons.cancel"),
  }).then(() => {
    emit("targetId", position.id!);
  });
};
</script>
<template>
  <VDropdown
    v-if="[InputTypeEnum.SNEAK, InputTypeEnum.MOVE].includes(currentMsgType!)"
    :delay="0"
    :triggers="['click']"
    placement="top"
  >
    <slot>
      <button
        class="btn btn-xs btn-neutral rounded border-base-content/10 w-fit flex-nowrap"
      >
        <span
          :class="{ 'text-base-content/50': actionTargets?.length === 0 }"
          class="line-clamp-1 flex-grow"
        >
          {{ playerCurrentPosition?.name }}
        </span>
        <i class="iconify mingcute--down-small-line shrink-0" />
      </button>
    </slot>
    <template #popper>
      <div
        class="flex flex-col join join-vertical text-base-content rounded-none p-1"
      >
        <slot name="header" />
        <div
          v-for="position in playerCurrentPosition?.destinationzz || []"
          :key="position.id"
          class="min-w-24 flex gap-2 items-center px-2 py-1 hover:bg-base-200 rounded cursor-pointer overflow-hidden"
          v-close-popper
          @click="onClickPosition(position)"
        >
          <!-- <span
            class="w-4 aspect-square flex items-center justify-center shrink-0"
          >
            <i
              v-if="actionTargetSet.has(position.id)"
              class="iconify mingcute--check-line text-xs"
            />
          </span> -->
          <span class="flex-grow line-clamp-1">
            {{ position.name }}
            <!-- ({{
              getPlayerListByPosition(position)
                ?.map((v) => v.mate!.name)
                .join(", ")
            }}) -->
          </span>
        </div>
      </div>
    </template>
  </VDropdown>
  <VDropdown
    v-else-if="characterList.length > 0"
    :delay="0"
    :triggers="['click']"
    placement="top"
  >
    <slot>
      <button
        class="btn btn-xs btn-neutral rounded border-base-content/10 w-fit flex-nowrap"
      >
        <span
          :class="{ 'text-base-content/50': actionTargets?.length === 0 }"
          class="line-clamp-1 flex-grow"
        >
          {{
            actionTargets!.length > 0
              ? actionTargets?.join(", ")
              : characterList[0].mate!.name
          }}
        </span>
        <i class="iconify mingcute--down-small-line shrink-0" />
      </button>
    </slot>
    <template #popper>
      <div
        class="flex flex-col join join-vertical text-base-content rounded-none p-1"
      >
        <slot name="header" />
        <div
          v-for="c in characterList"
          :key="c.id"
          :class="[
            'min-w-24 flex gap-2 items-center px-2 py-1 hover:bg-base-200 rounded cursor-pointer overflow-hidden',
            c.currentPositionId === playerCurrentPosition?.id
              ? ''
              : ' opacity-65 cursor-not-allowed',
          ]"
          @click="onClickCharacter(c)"
        >
          <span
            class="w-4 aspect-square flex items-center justify-center shrink-0"
          >
            <i
              v-if="actionTargets?.includes(c.mate!.name)"
              class="iconify mingcute--check-line text-xs"
            />
          </span>
          <span class="flex-grow line-clamp-1">
            {{ c.mate!.name }}({{ c.currentPosition?.name }})
          </span>
        </div>
      </div>
    </template>
  </VDropdown>
</template>
